import { resolveMediaUrl } from "./mediaUrl";
import { pickCoverGradient, stableHash } from "./bandCoverGradient";
import {
    buildBandStyleTags,
    formatStyleTagForDisplay,
} from "./styleTags";
import { resolveUserAvatarUrl, resolveUserDisplayName, resolveUserUsername } from "./userIdentity";
import { isActiveBandMemberEntry } from "./bandMembership";

function pickTint(userId) {
    return (stableHash(userId) % 6) + 1;
}

function normalizeString(value) {
    return typeof value === "string" ? value.trim() : "";
}

function resolveMapField(value) {
    return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function pickImageUrl(value) {
    if (typeof value === "string") {
        return value.trim();
    }
    if (value && typeof value === "object") {
        return normalizeString(value.url) || normalizeString(value.imageUrl);
    }
    return "";
}

function resolveImageUrlFromCandidates(candidates) {
    const rawUrl = candidates
        .map(pickImageUrl)
        .find(Boolean);
    return rawUrl ? resolveMediaUrl(rawUrl) : "";
}

function buildBandAvatarUrl(band) {
    return resolveImageUrlFromCandidates([
        band?.images?.avatar,
        band?.avatarUrl,
        band?.iconUrl,
    ]);
}

function buildBandImageUrl(band) {
    return resolveImageUrlFromCandidates([
        band?.images?.avatar,
        band?.avatarUrl,
        band?.iconUrl,
        band?.coverImageUrl,
        band?.coverImage,
        band?.images?.cover,
    ]);
}

function buildLocationLabel(band) {
    const city = normalizeString(band?.city);
    const stateOrRegion = normalizeString(band?.stateOrRegion);
    if (city || stateOrRegion) {
        return [city, stateOrRegion].filter(Boolean).join(", ");
    }

    const display = normalizeString(band?.locationDisplay);
    if (!display) {
        return "";
    }
    return display
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean)
        .slice(0, 2)
        .join(", ");
}

function buildLocationSearchText(band) {
    return [
        band?.locationDisplay,
        band?.city,
        band?.stateOrRegion,
        band?.country,
    ]
        .map(normalizeString)
        .filter(Boolean)
        .join(", ");
}

function adaptMember(member) {
    if (!member) return null;
    const profile = member.user || {};
    return {
        id: member.userId || profile.id,
        username: resolveUserUsername(member),
        displayName: resolveUserDisplayName(member, "?"),
        avatarUrl: resolveUserAvatarUrl(member),
        tint: pickTint(member.userId || profile.id || profile.username || ""),
        role: member.mainInstrument || profile.mainInstrument || null,
    };
}

function resolveMemberCount(band, members) {
    const rawCount = band?.memberCount;
    if (rawCount !== undefined && rawCount !== null) {
        const numericCount = Number(rawCount);
        if (Number.isFinite(numericCount) && numericCount >= 0) {
            return numericCount;
        }
    }
    return members.length;
}

export default function adaptBandToCatalogCard(band) {
    const id = band?.id || band?.objectId;
    if (!band || !id) return null;
    const styleTags = buildBandStyleTags(band);
    const members = Array.isArray(band.members)
        ? band.members
            .filter((member) => isActiveBandMemberEntry(member, { treatMissingAsActive: true }))
            .map(adaptMember)
            .filter(Boolean)
        : [];

    return {
        id,
        handle: band.handle || band.publicHandle || null,
        name: band.name || "",
        style: formatStyleTagForDisplay(styleTags[0]) || "",
        styleTags,
        location: buildLocationLabel(band),
        locationSearchText: buildLocationSearchText(band),
        description: band.description || "",
        cover: pickCoverGradient(id),
        avatarUrl: buildBandAvatarUrl(band),
        imageUrl: buildBandImageUrl(band),
        isOwner: band.currentUserIsOwner === true,
        currentUserIsMember: band.currentUserIsMember === true,
        currentUserMembershipStatus: band.currentUserMembershipStatus || null,
        seekingMembers: resolveMapField(band.seekingMembers),
        instrumentsNeeded: resolveMapField(band.instrumentsNeeded),
        members,
        memberCount: resolveMemberCount(band, members),
    };
}
